import { handleRequest } from "../utilities/requestHandler";
import { SERVER_BASE_URL } from "../config";

export const getDocumentCategories = () =>
    handleRequest("get", "/documents/categories");

// Lightweight category list (no documents) for admin reassignment/upload modals
export const getDocumentCategoryList = (searchTerm = "") => {
    const query = `${searchTerm || ""}`.trim();
    return handleRequest("get", "/documents/categories/list", query ? { q: query } : null);
};

export const getDocuments = (siteId) =>
    handleRequest("post", "/documents/getDocuments", {
        site_id: siteId,
    });

export const downloadDocument = (documentName) =>
    handleRequest("get", `/documents/downloadDocument/${documentName}`, null, {
        responseType: "blob",
    });

/**
 * NEW: Get category counts with optional filters
 */
export const getCategoryCounts = (siteId = null, options = {}) => {
    const { machineId, searchTerm , siteIds , searchBysiteIds} = options;

    const payload = {};

    if (siteId) {
        payload.site_id = siteId;
    }
    else {
        payload.siteIds= siteIds;
        payload.searchBysiteIds=searchBysiteIds; 
    }

    if (machineId && machineId !== 'all') {
        payload.machine_id = machineId;
    }

    if (searchTerm && searchTerm.trim()) {
        payload.search_term = searchTerm.trim();
    }

    
    return handleRequest("post", "/documents/getCategoryCounts", payload);
};

export const getDocumentsMetaData = (siteId = null, options = {}) => {
    const {
        page = 1,
        limit = 10,
        sortBy = 'uploaded_at',
        sortOrder = 'DESC',
        category,      // NEW: Add category
        machineId,     // NEW: Add machine filter
        searchTerm,     // NEW: Add search filter
        siteIds,
        searchBysiteIds
    } = options;

    const body = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
        site_ids: siteIds,
        searchBysiteIds:searchBysiteIds
    };


    if (siteId !== null && siteId !== undefined) {
        body.site_id = siteId;
    }

    // NEW: Add category filter
    if (category) {
        body.category = category;
    }

    // NEW: Add machine filter
    if (machineId && machineId !== 'all') {
        body.machine_id = machineId;
    }

    // NEW: Add search filter
    if (searchTerm && searchTerm.trim()) {
        body.search_term = searchTerm.trim();
    }

    return handleRequest("post", "/documents/getDocumentsMetaData", body);
};
export const uploadDocument = (formData) =>
    handleRequest("post", "/documents/uploadDocument", formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });

export const updateDocument = (documentId, formData) =>
    handleRequest("put", `/documents/update/${documentId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });

export const deleteDocument = (documentId) =>
    handleRequest("delete", `/documents/deleteDocument/${documentId}`);

export const deleteDocumentsByMachineNumber = (machineNumber, siteId = null) =>
    handleRequest("post", "/documents/admin/delete-by-machine", {
        machine_number: machineNumber,
        ...(siteId ? { site_id: siteId } : {}),
    });

export const searchMachines = (searchQuery, page, limit = 50) =>
    handleRequest("post", "/machines/search", { q: searchQuery, page, limit });

export const getDocumentAudience = (payload) =>
    handleRequest("post", "/documents/audience", payload);

export const createDocumentCategory = (payload) =>
    handleRequest("post", "/documents/categories", payload);

export const updateDocumentCategory = (categoryId, payload) =>
    handleRequest("put", `/documents/categories/${categoryId}`, payload);

export const deleteDocumentCategory = (categoryId, payload = {}) =>
    handleRequest("delete", `/documents/categories/${categoryId}`, payload);

export const updateCategoryDocuments = (categoryId, payload) =>
    handleRequest("post", `/documents/categories/${categoryId}/documents`, payload);

export const reassignDocumentsToCategory = (payload) =>
    handleRequest("post", "/documents/categories/reassign-documents", payload);

export const uploadAdminPortalDocument = (formData) =>
    handleRequest("post", "/documents/admin/portal-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });

export const reassignDocumentsToMachine = (payload) =>
    handleRequest("post", "/documents/admin/reassign-machine", payload);

// ==================== FAVORITES ====================

export const getFavorites = (siteId) => {
    return handleRequest('get', `/documents/favorites${siteId ? `?siteId=${siteId}` : ''}`);
};

export const addFavorite = (documentId, siteId) => {
    return handleRequest('post', `/documents/favorites/${documentId}`, { siteId });
};

export const removeFavorite = (documentId, siteId) => {
    return handleRequest('delete', `/documents/favorites/${documentId}`, { siteId });
};

// ==================== RECENTLY VIEWED ====================

export const getRecentlyViewed = (limit = 50, siteId) => {
    return handleRequest('get', `/documents/recently-viewed?limit=${limit}${siteId ? `&siteId=${siteId}` : ''}`);
};

export const trackDocumentView = (documentId, siteId) => {
    return handleRequest('post', `/documents/recently-viewed/${documentId}`, { siteId });
};

export const clearRecentlyViewed = (siteId = null) => {
    const body = siteId ? { siteId } : {};
    return handleRequest('delete', '/documents/recently-viewed', body);
};


// ==================== HISTORICAL ARCHIVE ====================

/**
 * Get directory listing for historical archive.
 * `path` must start with "/" (root = "/").
 * Example: "/Historical Documentation/01-Dough Systems"
 */
export const getArchiveDirectory = (path = "/") =>
    handleRequest("get", `/documents/archive`, null, {
        params: { path },
    });

/**
* Download/open a file from the historical archive.
* Returns a blob you can open or save.
*/

export const downloadArchiveFile = (path) =>
    handleRequest("get", `/documents/archive/file`, null, {
        params: { path },
        responseType: "blob",
    });


/**
 * Get the backend base URL
 * 
 * BEST PRACTICE:
 * - Use same-origin in production
 * - Use localhost only in development
 */
const getBackendUrl = () => {
    return SERVER_BASE_URL || window.location.origin;
};

/**
 * Generate URL for training files with authentication token.
 * Appends JWT token as query parameter for window.open() compatibility.
 */
export const getTrainingFileUrl = (blobPath) => {
    if (!blobPath) return null;

    const ext = blobPath.split(".").pop().toLowerCase();
    const baseUrl = getBackendUrl();

    let url;

    if (ext === "html") {
        url = `${baseUrl}/api/documents/azure/serve-html-with-sas?blobPath=${encodeURIComponent(blobPath)}`;
    } else if (["pdf", "mp4", "webm", "mp3", "txt", "json", "xml"].includes(ext)) {
        url = `${baseUrl}/api/documents/azure/proxy/${encodeURIComponent(blobPath)}`;
    } else {
        url = `${baseUrl}/api/documents/archive/file?path=${encodeURIComponent(blobPath)}`;
    }

    return url;
};

/**
 * Extract title from HTML training file.
 */
export const extractHtmlTitle = (blobPath) =>
    handleRequest("get", "/documents/azure/extract-html-title", null, {
        params: { blobPath },
    });

/**
 * Get container-level SAS URL for HTML with all resources.
 */
export const getHtmlWithResources = (blobPath) =>
    handleRequest("get", "/documents/azure/access-html-with-resources", null, {
        params: { blobPath },
    });

/**
 * Download a document by ID (via proxy).
 * This hits your backend controller `downloadDocumentProxy`  
 * which streams the file from Azure/local storage with proper headers. 
 **/
export const downloadDocumentById = (documentId) =>
    handleRequest("get", `/documents/download/${documentId}`, null, {
        responseType: "blob",
    });

// ==================== NEW SERVICE FUNCTIONS ====================

/**
 * Download a document via backend proxy.
 * Used for bulk downloads and single file downloads.
 * Returns blob data.
 */
export const downloadDocumentProxy = (documentId) =>
    handleRequest("get", `/documents/download-proxy/${documentId}`, null, {
        responseType: "blob",
    });

export const downloadArchiveFileProxy = (filePath) =>
    handleRequest("get", `/documents/archive/download-proxy`, null, {
        params: { path: filePath },
        responseType: "blob",
        timeout: 300000, 
    });

/**
 * Get SAS URL for a document by ID.
 * Used in DocumentViewModal for secure file access.
 * Optional: expiry_hours query parameter (default: 1 hour)
 */
export const getDocumentSasUrl = (documentId, expiryHours = null) => {
    const params = expiryHours ? { expiry_hours: expiryHours } : {};
    return handleRequest("get", `/documents/${documentId}/sas-url`, null, {
        params,
    });
};

/**
 * Serve HTML file with SAS token.
 * Used for previewing HTML files in iframes.
 * This endpoint returns the HTML content with embedded SAS URLs for resources.
 */
export const serveHtmlWithSas = (blobPath) => {
    // This returns a URL string, not a handleRequest call
    // Because it's used directly in iframe src attributes
    const baseUrl = getBackendUrl();
    const params = new URLSearchParams({ blobPath });
    return `${baseUrl}/api/documents/azure/serve-html-with-sas?${params.toString()}`;
};

export const exportHtmlManualPdf = (blobPath) =>
    handleRequest("post", "/documents/export-html-pdf", { blobPath }, {
        responseType: "blob",
        timeout: 300000,
    });

/**
 * Get archive file with authentication.
 * Used in HistoricalFileViewModal for fetching files from archive.
 * Returns SAS URL and file metadata.
 */
export const getArchiveFileAccess = (path) =>
    handleRequest("get", `/documents/archive/file`, null, {
        params: { path },
    });
